import React, { useId } from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      className = '',
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    const containerClass = fullWidth ? 'w-full' : '';
    const inputBaseClasses =
      'bg-vb-surface border border-vb-border rounded-lg px-3 py-2 text-vb-text-primary placeholder-vb-text-tertiary transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-vb-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed dark:bg-vb-surface-dark dark:border-vb-border-dark dark:text-vb-text-primary-dark dark:placeholder-vb-text-tertiary-dark';

    const inputErrorClasses = error ? 'border-vb-danger focus:ring-vb-danger' : '';

    const inputIconClasses = leftIcon ? 'pl-10' : rightIcon ? 'pr-10' : '';

    // Build aria-describedby based on what's present
    const describedBy = error ? errorId : helperText ? helperId : undefined;

    return (
      <div className={`flex flex-col gap-1.5 ${containerClass}`}>
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-vb-text-tertiary dark:text-vb-text-tertiary-dark">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={`${inputBaseClasses} ${inputErrorClasses} ${inputIconClasses} ${className} ${fullWidth ? 'w-full' : ''}`}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-vb-text-tertiary dark:text-vb-text-tertiary-dark">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p id={errorId} className="text-sm text-vb-danger" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={helperId} className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helperText, fullWidth = false, className = '', disabled, id, ...props }, ref) => {
    const generatedId = useId();
    const textareaId = id || generatedId;
    const errorId = `${textareaId}-error`;
    const helperId = `${textareaId}-helper`;

    const containerClass = fullWidth ? 'w-full' : '';
    const textareaBaseClasses =
      'bg-vb-surface border border-vb-border rounded-lg px-3 py-2 text-vb-text-primary placeholder-vb-text-tertiary transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-vb-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed resize-y dark:bg-vb-surface-dark dark:border-vb-border-dark dark:text-vb-text-primary-dark dark:placeholder-vb-text-tertiary-dark';

    const textareaErrorClasses = error ? 'border-vb-danger focus:ring-vb-danger' : '';

    const describedBy = error ? errorId : helperText ? helperId : undefined;

    return (
      <div className={`flex flex-col gap-1.5 ${containerClass}`}>
        {label && (
          <label
            htmlFor={textareaId}
            className="text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={`${textareaBaseClasses} ${textareaErrorClasses} ${className} ${fullWidth ? 'w-full' : ''}`}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...props}
        />
        {error && (
          <p id={errorId} className="text-sm text-vb-danger" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={helperId} className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  options: { value: string; label: string }[];
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    { label, error, helperText, fullWidth = false, options, className = '', disabled, id, ...props },
    ref
  ) => {
    const generatedId = useId();
    const selectId = id || generatedId;
    const errorId = `${selectId}-error`;
    const helperId = `${selectId}-helper`;

    const containerClass = fullWidth ? 'w-full' : '';
    const selectBaseClasses =
      'bg-vb-surface border border-vb-border rounded-lg px-3 py-2 text-vb-text-primary transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-vb-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed appearance-none dark:bg-vb-surface-dark dark:border-vb-border-dark dark:text-vb-text-primary-dark';

    const selectErrorClasses = error ? 'border-vb-danger focus:ring-vb-danger' : '';

    const describedBy = error ? errorId : helperText ? helperId : undefined;

    return (
      <div className={`flex flex-col gap-1.5 ${containerClass}`}>
        {label && (
          <label
            htmlFor={selectId}
            className="text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={`${selectBaseClasses} ${selectErrorClasses} ${className} ${fullWidth ? 'w-full' : ''} pr-10`}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            {...props}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-vb-text-tertiary dark:text-vb-text-tertiary-dark">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
        {error && (
          <p id={errorId} className="text-sm text-vb-danger" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={helperId} className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
